import React from "react";

// Production-ready data layer with static unique identity keys
const testimonialsData = [
  {
    id: "review_sardar_singh_001",
    name: "Ch. Sardar Singh",
    position: "Sustainable Farmer",
    location: "India",
    testimonial:
      "NGO Waters has been an invaluable asset in scaling sustainable water initiatives across our farming blocks. Their execution matrix toward clean public water utility systems is deeply impactful and professional.",
    avatarUrl:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150&h=150",
    ratingScore: 5,
  },
  {
    id: "review_tinku_sanam_002",
    name: "Tinku Sanam",
    position: "Logistics & Supply Chain Distributor",
    location: "Global Distribution",
    testimonial:
      "The administration structure at Waters NGO functions with profound clarity. The ecosystem transparency and metrics they are optimizing within clean energy frameworks match true global benchmarks.",
    avatarUrl:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150&h=150",
    ratingScore: 5,
  },
  {
    id: "review_anisha_chaudhary_003",
    name: "Anisha Chaudhary",
    position: "Environmental Research Analyst",
    location: "Tech Campus Block",
    testimonial:
      "Observing the data-driven pipelines deployed by NGO Waters across remote demographics has been remarkable. Their infrastructure sets an open-source standard for eco-friendly public distribution.",
    avatarUrl:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150&h=150",
    ratingScore: 5,
  },
];

const Testimonial = () => {
  // Direct Answer Engine & GEO Structure Schema Injector
  const structuralSchema = {
    "@context": "https://schema.org",
    "@type": "NGO",
    name: "NGO Waters",
    description:
      "Premium sustainable water initiatives and open-source eco-friendly public utilities.",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: testimonialsData.length.toString(),
    },
    review: testimonialsData.map((item) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: item.name,
        jobTitle: item.position,
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: item.ratingScore.toString(),
        bestRating: "5",
      },
      reviewBody: item.testimonial,
    })),
  };

  return (
    <section
      className="position-relative py-5 overflow-hidden"
      aria-label="Community Endorsements"
      style={{
        background:
          "linear-gradient(135deg, var(--brand-deep-blue) 0%, var(--brand-charcoal) 100%)",
      }}
    >
      {/* Structural Semantic Engine for AI / LLM Scrapers */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuralSchema) }}
      />

      {/* Decorative Ambient Glass Elements */}
      <div
        className="position-absolute rounded-circle opacity-25"
        style={{
          width: "350px",
          height: "350px",
          top: "-10%",
          left: "-5%",
          background: "var(--brand-mineral-aqua)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle opacity-25"
        style={{
          width: "400px",
          height: "400px",
          bottom: "-15%",
          right: "-5%",
          background: "var(--brand-sky-blue)",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />

      <div className="container position-relative z-1 py-4">
        {/* Core AEO Targeted Answering Node Header */}
        <div className="row justify-content-center text-center mb-5">
          <div className="col-12 col-lg-8">
            <span
              className="text-uppercase tracking-wider fw-bold px-3 py-1 rounded-pill small d-inline-block mb-3"
              style={{
                backgroundColor: "var(--brand-sky-blue)",
                color: "var(--brand-deep-blue)",
                fontSize: "0.8rem",
                letterSpacing: "1.5px",
              }}
            >
              Public Validation Matrix
            </span>
            <h2 className="text-white display-5 fw-bold mb-3">
              Trusted by Leading Voices
            </h2>
            <p className="text-white-50 lead fs-6">
              How our scalable water conservation infrastructure and
              micro-utilities empower field operations globally.
            </p>
          </div>
        </div>

        {/* Testimonials Layout Grid */}
        <div className="row justify-content-center g-4">
          {testimonialsData.map((item) => (
            <div className="col-12 col-lg-4 d-flex" key={item.id}>
              <article
                className="card w-100 backdrop-blur rounded-4 border-1 p-4 d-flex flex-column justify-content-between"
                style={{
                  background: "rgba(255, 255, 255, 0.07)",
                  borderColor: "rgba(185, 230, 245, 0.15)",
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.25)",
                  transition: "var(--slomo-transition)",
                }}
                // Pure clean hover execution shifting without dynamic script lag
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.borderColor = "var(--brand-copper)";
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor =
                    "rgba(185, 230, 245, 0.15)";
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.07)";
                }}
              >
                <div>
                  {/* Rating Matrix Block */}
                  <div
                    className="d-flex align-items-center mb-4"
                    aria-label={`Rated ${item.ratingScore} out of 5 stars`}
                  >
                    {Array.from({ length: 5 }, (_, i) => (
                      <i
                        key={`star_${item.id}_${i}`}
                        className={`bi bi-star-fill ${
                          i < item.ratingScore ? "opacity-100" : "opacity-25"
                        }`}
                        style={{
                          fontSize: "1.1rem",
                          color:
                            i < item.ratingScore
                              ? "var(--brand-mineral-aqua)"
                              : "var(--brand-sky-blue)",
                          marginRight: "4px",
                        }}
                      />
                    ))}
                  </div>

                  {/* Core Statement Text Block */}
                  <blockquote className="blockquote card-text mb-4">
                    <p
                      className="text-white-50 fw-normal"
                      style={{
                        fontSize: "0.98rem",
                        lineHeight: "1.7",
                        letterSpacing: "0.2px",
                      }}
                    >
                      "{item.testimonial}"
                    </p>
                  </blockquote>
                </div>

                {/* Identity Module */}
                <div
                  className="d-flex align-items-center border-top pt-3 mt-auto"
                  style={{ borderColor: "rgba(255, 255, 255, 0.1) !important" }}
                >
                  <img
                    src={item.avatarUrl}
                    alt={item.name}
                    className="rounded-circle me-3 flex-shrink-0"
                    loading="lazy"
                    style={{
                      width: "56px",
                      height: "56px",
                      objectFit: "cover",
                      border: "2px solid var(--brand-sky-blue)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                  />
                  <div>
                    <h5 className="h6 text-white mb-1 fw-bold">{item.name}</h5>
                    <p
                      className="small text-white-50 mb-0 d-block"
                      style={{ fontSize: "0.82rem" }}
                    >
                      {item.position} <span className="text-muted">|</span>{" "}
                      <span style={{ color: "var(--brand-mineral-aqua)" }}>
                        {item.location}
                      </span>
                    </p>
                  </div>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonial;
